import { HTMLListItem, Adapter, SelectionBehavior } from "./interfaces";

export enum ControllerState {
    UNKNOWN,
    UNSELECTED,
    SELECTED,
    MIXED,
}

interface HTMLControllerListItem extends HTMLListItem {
    mixed?: boolean;
    controller?: boolean;
    group: string;
}

export class MultiSelectionBehavior implements SelectionBehavior {
    constructor(private adapter: Adapter<HTMLControllerListItem>) {}

    clearValue() {
        const items = this.adapter.getItems();
        items.forEach((item) => {
            item.selected = false;
            item.mixed = false;
        });
    }

    setValue(value: string[]) {
        const set = new Set(value);
        const items = this.adapter.getItems() as HTMLControllerListItem[];

        // set items within two phases:
        // 1. select all values in the set without checking anything
        // 2. check controller states (use a for loop to allow early exit)

        items.forEach((item) => (item.selected = set.has(item.value)));

        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (this.isRootController(item)) {
                if (item.selected) {
                    items.forEach((item) => {
                        item.selected = true;
                        item.mixed = false;
                    });
                    return;
                }
            }

            if (this.isController(item)) {
                const children = this.getControllerChildren(item, items);
                if (item.selected) {
                    children.forEach((item) => (item.selected = true));
                } else {
                    const state = this.getControllerStateByChildren(children);
                    this.setControllerState(item, state);
                }
            }
        }
    }

    select(item: HTMLControllerListItem) {
        const items = this.adapter.getItems() as HTMLControllerListItem[];
        item.selected = !item.selected;
        const isController = this.isController(item);
        const isRootController = isController && !item.group;

        if (isController) {
            this.setControllerState(
                item,
                item.selected
                    ? ControllerState.SELECTED
                    : ControllerState.UNSELECTED
            );

            const children = this.getControllerChildren(item, items);
            children.forEach((child) => (child.selected = item.selected));
        } else {
            const { controller, children } = this.getControllerWithGroup(
                item.group,
                items
            );
            if (controller) {
                const state = this.getControllerStateByChildren(children);
                this.setControllerState(controller, state);
            }
        }

        if (!isRootController) {
            const rootController = items.find(
                (item) => item.controller && !item.group
            );
            const state = this.getControllerStateByChildren(items);
            this.setControllerState(rootController, state);
        }

        const val = [];
        for (let i = 0; i < items.length; i++) {
            const element = items[i];
            if (element.selected) {
                val.push(element.value);
            }
        }

        this.adapter.setValue(val);
    }

    private isController(item: HTMLListItem) {
        return (item as HTMLControllerListItem).controller;
    }

    private isRootController(item: HTMLControllerListItem) {
        return item.controller && !item.group;
    }

    private countSelectedReducer(count: number, item: HTMLListItem) {
        return item && item.selected ? count + 1 : count;
    }

    /**
     * Gets the controller and its children within a single for-loop
     * @param group The name of the group
     * @param items All items
     */
    private getControllerWithGroup(
        group: string,
        items: HTMLControllerListItem[]
    ) {
        let controller;
        let children = [];

        for (let i = 0; i < items.length; i++) {
            const element = items[i];
            if (element.controller && element.group === group) {
                controller = element;
            } else if (element.group === group) {
                children.push(element);
            }
        }
        return {
            controller,
            children,
        };
    }

    private getControllerChildren(
        controller: HTMLControllerListItem,
        items: HTMLControllerListItem[]
    ) {
        if (controller.group) {
            return items.filter(
                (item) => !item.controller && item.group === controller.group
            );
        } else {
            return items.filter((item) => item !== controller);
        }
    }

    private getControllerStateByChildren(
        children?: HTMLListItem[]
    ): ControllerState {
        if (!children) return ControllerState.UNKNOWN;

        const childrenCount = children.length;
        const selectedChildrenCount = children.reduce(
            this.countSelectedReducer,
            0
        );

        if (childrenCount === selectedChildrenCount) {
            return ControllerState.SELECTED;
        } else if (selectedChildrenCount === 0) {
            return ControllerState.UNSELECTED;
        } else {
            return ControllerState.MIXED;
        }
    }

    private setControllerState(
        controller: HTMLControllerListItem,
        state: ControllerState
    ) {
        if (!controller) return;

        switch (state) {
            case ControllerState.SELECTED:
                controller.mixed = false;
                controller.selected = true;
                break;
            case ControllerState.UNSELECTED:
                controller.mixed = false;
                controller.selected = false;
                break;
            case ControllerState.MIXED:
                controller.mixed = true;
                controller.selected = false;
                break;
            default:
                break;
        }
    }
}
